import { FC, HTMLAttributes } from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useIsZigzag } from '@common/device-manager';
import { Typography, web_max_width } from '@common/styles';
import { isSupportedOpenExternalBrowser, openExternalBrowser } from '@common/utils';

import { BROWSER_HREF } from '../constants/link-url';

export const Footer: FC<React.PropsWithChildren<HTMLAttributes<HTMLElement>>> = () => {
  const isZigzag = useIsZigzag();

  const handleEscrowLicenseClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isZigzag && isSupportedOpenExternalBrowser()) {
      e.preventDefault();
      openExternalBrowser(BROWSER_HREF.escrow_license);
    }
  };

  const handlePaymentGuaranteeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isZigzag && isSupportedOpenExternalBrowser()) {
      e.preventDefault();
      openExternalBrowser(BROWSER_HREF.payment_guarantee);
    }
  };

  return (
    <FooterContainer>
      <Wrapper>
        <Title className={Typography.BODY_14_BOLD}>(주)카카오스타일 사업자 정보</Title>
        <Info className={Typography.CAPTION_12_REGULAR}>
          <li>
            <Label>대표이사</Label>
            <Description>서정훈</Description>
          </li>
          <li>
            <Label>주소</Label>
            <Description>경기 성남시 분당구 판교역로 235, H스퀘어 N동 6층</Description>
          </li>
          <li>
            <Label>문의전화</Label>
            <Description>02-1670-8050</Description>
          </li>
          <li>
            <Label>이메일</Label>
            <Description>info@kakaostyle.com</Description>
          </li>
          <li>
            <Label>사업자등록번호</Label>
            <Description>214-88-91525</Description>
          </li>
          <li>
            <Label>통신판매업 신고번호</Label>
            <Description>2024-성남분당A-0172</Description>
          </li>
          <li>
            <Label>호스팅 사업자</Label>
            <Description>(주)카카오스타일</Description>
          </li>
        </Info>
        <List className={Typography.CAPTION_12_MEDIUM}>
          <ListItem>
            <a href={BROWSER_HREF.business_info} target='_blank' rel='noreferrer'>
              사업자 정보 조회
            </a>
          </ListItem>
          <ListItem>
            <a href={BROWSER_HREF.service_terms} target='_blank' rel='noreferrer'>
              이용약관
            </a>
          </ListItem>
          <ListItem>
            <a href={BROWSER_HREF.privacy} target='_blank' rel='noreferrer'>
              개인정보처리방침
            </a>
          </ListItem>
        </List>
        <Paragraph as='div' className={Typography.CAPTION_11_REGULAR}>
          ㈜카카오스타일은 통신판매중개자로서 통신판매의 당사자가 아닙니다.
          <br />
          따라서, 등록된 상품, 거래정보 및 거래에 대하여 ㈜카카오스타일은
          <br />
          어떠한 책임도 지지 않습니다.
          <br />
          <br />
          Copyright © kakaostyle Corp. All rights reserved.
          <br />
          <br />
          <EscrowWrapper className={Typography.CAPTION_11_REGULAR}>
            <EscrowText>
              당사는 고객님의 안전거래를 위해 관련 법률에 의거하여 나이스정보통신(주)의 에스크로 서비스를 적용하고
              있습니다.
            </EscrowText>
            <EscrowLink
              className={Typography.CAPTION_12_SEMIBOLD}
              href={BROWSER_HREF.escrow_license}
              target='_blank'
              rel='noreferrer'
              onClick={handleEscrowLicenseClick}
            >
              서비스 가입사실 확인
            </EscrowLink>
          </EscrowWrapper>
          <EscrowWrapper className={Typography.CAPTION_11_REGULAR}>
            <EscrowText>
              당사는 고객님이 현금 결제한 금액에 대해 신한은행과 채무지급보증 계약을 체결하여 안전거래를 보장하고
              있습니다.
            </EscrowText>
            <EscrowLink
              className={Typography.CAPTION_12_SEMIBOLD}
              href={BROWSER_HREF.payment_guarantee}
              target='_blank'
              rel='noreferrer'
              onClick={handlePaymentGuaranteeClick}
            >
              서비스 가입사실 확인
            </EscrowLink>
          </EscrowWrapper>
        </Paragraph>
      </Wrapper>
      <FooterBackground />
    </FooterContainer>
  );
};

const FooterBackground = styled.div`
  background: ${vars.color.backgroundOnSurface};
  position: fixed;
  bottom: 0;
  left: 50%;
  max-width: ${web_max_width}px;
  width: 100%;
  height: 50vh;
  transform: translateX(-50%);
  z-index: -1;
`;

const FooterContainer = styled.footer`
  background: ${vars.color.backgroundOnSurface};
  position: relative;
`;

const Wrapper = styled.div`
  margin: auto;
  position: relative;
  max-width: ${web_max_width}px;
  padding: 30px 16px 146px;
  padding-bottom: calc(env(safe-area-inset-bottom) + 146px);
  padding-bottom: calc(constant(safe-area-inset-bottom) + 146px);
  z-index: 1;
`;

const Title = styled.h2`
  color: ${vars.color.gray400};
  margin-bottom: 16px;
`;

const Info = styled.ul`
  color: ${vars.color.gray400};
  padding-bottom: 16px;
  margin: 0 0 16px 0;
  border-bottom: 1px solid ${vars.color.fillSecondary};

  &::after {
    content: '';
    display: block;
    clear: both;
  }
  li {
    display: flex;
    margin-bottom: 4px;
  }
`;

const Label = styled.span`
  white-space: pre-wrap;
`;

const Description = styled.div`
  margin-left: 12px;
`;

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const ListItem = styled.li`
  color: ${vars.color.gray400};

  &:last-of-type {
    color: ${vars.color.gray550};
  }

  &:not(:last-of-type)::after {
    content: '';
    display: inline-block;
    width: 2px;
    height: 2px;
    background: ${vars.color.gray300};
    margin: 0 8px;
    vertical-align: middle;
  }
`;

const Paragraph = styled.p`
  color: ${vars.color.gray400};
  white-space: pre-wrap;
`;

const EscrowWrapper = styled.div`
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const EscrowText = styled.span`
  margin-right: 8px;
  color: ${vars.color.gray400};
`;

const EscrowLink = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
  padding: 5px 12px;
  border: 1px solid ${vars.color.borderSecondary};
  color: ${vars.color.textQuaternary};
  border-radius: 4px;
  flex-shrink: 0;

  &:link,
  &:active,
  &:visited {
    color: ${vars.color.textQuaternary};
  }
`;
